import {type CSSProperties, useEffect, useRef} from "react";
import {css, cx} from "@panda/css";
import {motion, type TargetAndTransition, type Transition, useReducedMotion} from "framer-motion";

import {type AnimationEasing, type IKeyframeTrack, type IPoint, type IRect} from "@pg-mono/animation-utils";
import {noop} from "@pg-mono/nodash";

import {
    buildPlaneKeyframes,
    getPlaneAnimationDurationMs,
    getTrailRevealTimesMs,
    type IPlaneMotion,
    type IPlaneSilhouette
} from "./unlock_choice_sent_plane_motion";

type IllustrationTrackName = "opacity" | "rotate" | "scale" | "x" | "y";

type IllustrationTracks = Partial<Record<IllustrationTrackName, IKeyframeTrack>>;

export interface IUnlockChoiceSentLayer {
    asset: string;
    inset: [number, number, number, number];
    tracks?: IllustrationTracks;
}

export interface IUnlockChoiceSentPlane {
    asset: string;
    inset: [number, number, number, number];
    hull: IPoint[];
    details: IUnlockChoiceSentLayer[];
    motion: IPlaneMotion;
}

export const UNLOCK_CHOICE_SENT_DURATION_SECONDS = 2.5;

export const UNLOCK_CHOICE_SENT_SCENE = {width: 294.797, height: 114};

export const UNLOCK_CHOICE_SENT_TRAIL_FADE_MS = 60;

const TRAIL_CLEARANCE = 1;

const TRAIL_HIDDEN_PLANE_OPACITY = 0.05;

const TRACK_NAMES: IllustrationTrackName[] = ["opacity", "rotate", "scale", "x", "y"];

const CB: AnimationEasing = [0.5, 0, 0.5, 1];

export const UNLOCK_CHOICE_SENT_PLANE: IUnlockChoiceSentPlane = {
    asset: require("../../../images/unlock_choice_sent/plane.svg"),
    inset: [25.35, 0, 50.38, 86.3],
    hull: [
        {x: 0, y: 0.1909},
        {x: 0.0086, y: 0.1589},
        {x: 0.7737, y: 0.0122},
        {x: 1, y: 0.0002},
        {x: 1, y: 0.0346},
        {x: 0.7939, y: 0.6485},
        {x: 0.6545, y: 1},
        {x: 0.2703, y: 0.8087},
        {x: 0.2567, y: 0.7975}
    ],
    details: [{asset: require("../../../images/unlock_choice_sent/dot_right.svg"), inset: [22.539, 31.897, 71.899, 60.876]}],
    motion: {
        legs: [
            {
                start: 0.16,
                end: 0.6016,
                easeIn: 0.1,
                waypoints: [
                    {x: -270.319, y: 30.84, rotate: 87.376},
                    {x: -243.511, y: 52.093, rotate: 30.353},
                    {x: -186.519, y: 7.089, rotate: 4.171},
                    {x: -172.111, y: -2.428, rotate: -1.174}
                ]
            },
            {
                start: 0.6428,
                end: 0.975,
                approach: {start: 0.6304, rotate: 83.879},
                easeIn: 0.1,
                easeOut: 0.15,
                waypoints: [
                    {x: -101.34, y: 6.361, rotate: 86.91},
                    {x: -62.649, y: 27.629, rotate: 52.417},
                    {x: -42.186, y: 17.297, rotate: 33.709},
                    {x: -7.336, y: -0.298, rotate: -19.93}
                ]
            }
        ],
        scale: {
            values: [1, 0.7, 0.513, 0.4, 0.4, 1, 1],
            times: [0, 0.4, 0.56, 0.6304, 0.6428, 0.9084, 1],
            ease: [CB, CB, CB, "linear", CB, "linear"]
        },
        opacity: {
            values: [1, 1, 0, 0, 1, 1],
            times: [0, 0.56, 0.6016, 0.6304, 0.6428, 1],
            ease: ["linear", CB, "linear", CB, "linear"]
        },
        exit: {
            delayMs: 150,
            durationMs: 450,
            distance: 28,
            scale: 0.6,
            ease: [0.4, 0, 1, 1]
        }
    }
};

const FLIGHT_DURATION_MS = UNLOCK_CHOICE_SENT_DURATION_SECONDS * 1000;

export const UNLOCK_CHOICE_SENT_PLANE_DURATION_MS = getPlaneAnimationDurationMs(UNLOCK_CHOICE_SENT_PLANE.motion, FLIGHT_DURATION_MS);

const PLANE_KEYFRAMES = buildPlaneKeyframes(UNLOCK_CHOICE_SENT_PLANE.motion, FLIGHT_DURATION_MS);

const PLANE_ANIMATION_OPTIONS: KeyframeAnimationOptions = {
    duration: UNLOCK_CHOICE_SENT_PLANE_DURATION_MS,
    iterations: 1,
    fill: "forwards",
    easing: "linear"
};

const TIMELINE: Transition = {
    duration: UNLOCK_CHOICE_SENT_PLANE_DURATION_MS / 1000
};

const getInsetRect = (inset: [number, number, number, number]): IRect => {
    const [top, right, bottom, left] = inset;
    const {width, height} = UNLOCK_CHOICE_SENT_SCENE;

    return {
        left: (left / 100) * width,
        top: (top / 100) * height,
        width: ((100 - left - right) / 100) * width,
        height: ((100 - top - bottom) / 100) * height
    };
};

const getInsetCenter = (inset: [number, number, number, number]): IPoint => {
    const rect = getInsetRect(inset);

    return {x: rect.left + rect.width / 2, y: rect.top + rect.height / 2};
};

export const UNLOCK_CHOICE_SENT_PLANE_SILHOUETTE: IPlaneSilhouette = {
    box: getInsetRect(UNLOCK_CHOICE_SENT_PLANE.inset),
    hull: UNLOCK_CHOICE_SENT_PLANE.hull
};

const getTrailTrack = (revealMs: number): IKeyframeTrack => {
    const start = revealMs / UNLOCK_CHOICE_SENT_PLANE_DURATION_MS;
    const end = Math.min(1, (revealMs + UNLOCK_CHOICE_SENT_TRAIL_FADE_MS) / UNLOCK_CHOICE_SENT_PLANE_DURATION_MS);

    if (start <= 0) {
        return {values: [0, 1, 1], times: [0, end, 1], ease: [CB, "linear"]};
    }

    return {values: [0, 0, 1, 1], times: [0, start, end, 1], ease: ["linear", CB, "linear"]};
};

const withTrailTracks = (layers: IUnlockChoiceSentLayer[]): IUnlockChoiceSentLayer[] => {
    const revealTimesMs = getTrailRevealTimesMs(
        UNLOCK_CHOICE_SENT_PLANE.motion,
        FLIGHT_DURATION_MS,
        UNLOCK_CHOICE_SENT_PLANE_SILHOUETTE,
        layers.map((layer) => getInsetCenter(layer.inset)),
        {clearance: TRAIL_CLEARANCE, hiddenOpacity: TRAIL_HIDDEN_PLANE_OPACITY}
    );

    return layers.map((layer, index) => ({...layer, tracks: {opacity: getTrailTrack(revealTimesMs[index])}}));
};

export const UNLOCK_CHOICE_SENT_LAYERS = {
    still: [
        {asset: require("../../../images/unlock_choice_sent/circle.svg"), inset: [20.18, 41.65, 6.14, 29.85]},
        {asset: require("../../../images/unlock_choice_sent/spark_top_left.svg"), inset: [0.72, 53, 88.61, 44.93]},
        {asset: require("../../../images/unlock_choice_sent/spark_right.svg"), inset: [17.81, 40.9, 77.23, 55.34]},
        {asset: require("../../../images/unlock_choice_sent/envelopes.svg"), inset: [13.31, 41.75, 0, 28.39]},
        {asset: require("../../../images/unlock_choice_sent/dot_bottom_left.svg"), inset: [79.49, 67.99, 18.85, 31.28]},
        {asset: require("../../../images/unlock_choice_sent/spark_top_right.svg"), inset: [0, 45.39, 90.18, 51.92]}
    ] as IUnlockChoiceSentLayer[],
    leftDashes: withTrailTracks([
        {asset: require("../../../images/unlock_choice_sent/dash_left_01.svg"), inset: [51.39, 99.32, 43.46, 0]},
        {asset: require("../../../images/unlock_choice_sent/dash_left_02.svg"), inset: [59.62, 98.85, 35.31, 0.35]},
        {asset: require("../../../images/unlock_choice_sent/dash_left_03.svg"), inset: [67.21, 97.52, 28.3, 1.42]},
        {asset: require("../../../images/unlock_choice_sent/dash_left_04.svg"), inset: [73.71, 95.55, 22.66, 3.1]},
        {asset: require("../../../images/unlock_choice_sent/dash_left_05.svg"), inset: [78.46, 93.06, 18.75, 5.35]},
        {asset: require("../../../images/unlock_choice_sent/dash_left_06.svg"), inset: [81.31, 90.26, 16.6, 8]},
        {asset: require("../../../images/unlock_choice_sent/dash_left_07.svg"), inset: [82.05, 87.47, 16.11, 10.76]},
        {asset: require("../../../images/unlock_choice_sent/dash_left_08.svg"), inset: [79.96, 84.73, 17.37, 13.61]},
        {asset: require("../../../images/unlock_choice_sent/dash_left_09.svg"), inset: [76.11, 82.24, 20.47, 16.24]},
        {asset: require("../../../images/unlock_choice_sent/dash_left_10.svg"), inset: [70.92, 80.1, 25.31, 18.56]},
        {asset: require("../../../images/unlock_choice_sent/dash_left_11.svg"), inset: [65.1, 78.11, 30.88, 20.59]},
        {asset: require("../../../images/unlock_choice_sent/dash_left_12.svg"), inset: [58.91, 76.26, 36.83, 22.5]},
        {asset: require("../../../images/unlock_choice_sent/dash_left_13.svg"), inset: [53.14, 74.32, 42.99, 24.37]},
        {asset: require("../../../images/unlock_choice_sent/dash_left_14.svg"), inset: [47.81, 72.2, 48.51, 26.39]},
        {asset: require("../../../images/unlock_choice_sent/dash_left_15.svg"), inset: [43.16, 69.88, 53.48, 28.61]}
    ]),
    rightDashes: withTrailTracks([
        {asset: require("../../../images/unlock_choice_sent/dash_right_01.svg"), inset: [41.27, 40.13, 55.54, 58.36]},
        {asset: require("../../../images/unlock_choice_sent/dash_right_02.svg"), inset: [45.58, 37.67, 51.27, 60.75]},
        {asset: require("../../../images/unlock_choice_sent/dash_right_03.svg"), inset: [49.95, 35.35, 46.79, 63.17]},
        {asset: require("../../../images/unlock_choice_sent/dash_right_04.svg"), inset: [54.26, 32.9, 42.56, 65.52]},
        {asset: require("../../../images/unlock_choice_sent/dash_right_05.svg"), inset: [58.01, 30.34, 39.14, 68.06]},
        {asset: require("../../../images/unlock_choice_sent/dash_right_06.svg"), inset: [60.77, 27.55, 37, 70.72]},
        {asset: require("../../../images/unlock_choice_sent/dash_right_07.svg"), inset: [62.21, 24.7, 36.11, 73.54]},
        {asset: require("../../../images/unlock_choice_sent/dash_right_08.svg"), inset: [61.93, 21.83, 36.27, 76.34]},
        {asset: require("../../../images/unlock_choice_sent/dash_right_09.svg"), inset: [60.18, 19.09, 37.61, 79.18]},
        {asset: require("../../../images/unlock_choice_sent/dash_right_10.svg"), inset: [57.08, 16.48, 39.94, 81.91]},
        {asset: require("../../../images/unlock_choice_sent/dash_right_11.svg"), inset: [52.86, 14.05, 43.68, 84.5]},
        {asset: require("../../../images/unlock_choice_sent/dash_right_12.svg"), inset: [47.45, 11.95, 48.42, 86.76]}
    ])
};

const LAYERS: IUnlockChoiceSentLayer[] = [
    ...UNLOCK_CHOICE_SENT_LAYERS.still,
    ...UNLOCK_CHOICE_SENT_LAYERS.leftDashes,
    ...UNLOCK_CHOICE_SENT_LAYERS.rightDashes
];

const supportsWebAnimations = () => typeof HTMLElement !== "undefined" && typeof HTMLElement.prototype.animate === "function";

const getInsetStyle = (inset: [number, number, number, number]): CSSProperties => {
    const [top, right, bottom, left] = inset;

    return {
        "--layer-top": `${top}%`,
        "--layer-right": `${right}%`,
        "--layer-bottom": `${bottom}%`,
        "--layer-left": `${left}%`
    } as CSSProperties;
};

const getInitial = (tracks: IllustrationTracks): TargetAndTransition => {
    const initial: TargetAndTransition = {};

    for (const name of TRACK_NAMES) {
        const track = tracks[name];

        if (track) {
            initial[name] = track.values[0];
        }
    }

    return initial;
};

const getAnimate = (tracks: IllustrationTracks): TargetAndTransition => {
    const animate: TargetAndTransition = {};

    for (const name of TRACK_NAMES) {
        const track = tracks[name];

        if (track) {
            animate[name] = track.values;
        }
    }

    return animate;
};

const getTransition = (tracks: IllustrationTracks): Transition => {
    const transition: Partial<Record<IllustrationTrackName, Transition>> = {};

    for (const name of TRACK_NAMES) {
        const track = tracks[name];

        if (track) {
            transition[name] = {...TIMELINE, times: track.times, ease: track.ease};
        }
    }

    return transition;
};

interface ILayerProps {
    layer: IUnlockChoiceSentLayer;
    isAnimated: boolean;
}

const UnlockChoiceSentIllustrationLayer = (props: ILayerProps) => {
    const {layer, isAnimated} = props;
    const insetStyle = getInsetStyle(layer.inset);
    const image = <img src={layer.asset} alt="" className={imageStyle} />;

    if (!isAnimated || !layer.tracks) {
        return (
            <div className={layerStyle} style={insetStyle}>
                {image}
            </div>
        );
    }

    return (
        <motion.div
            className={cx(layerStyle, animatedLayerStyle)}
            style={insetStyle}
            initial={getInitial(layer.tracks)}
            animate={getAnimate(layer.tracks)}
            transition={getTransition(layer.tracks)}
            /**
                Any onUpdate makes framer-motion animate in JS instead of WAAPI. On WAAPI finish it cancels the animation at once but writes the final
                opacity a frame later, so for that frame the dash falls back to its `initial` opacity of 0 and the trail blinks
            **/
            onUpdate={noop}
        >
            {image}
        </motion.div>
    );
};

interface IPlaneProps {
    plane: IUnlockChoiceSentPlane;
    isAnimated: boolean;
}

const UnlockChoiceSentIllustrationPlane = (props: IPlaneProps) => {
    const {plane, isAnimated} = props;
    const planeRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const element = planeRef.current;

        if (!isAnimated || !element) {
            return;
        }

        const animation = element.animate(PLANE_KEYFRAMES, PLANE_ANIMATION_OPTIONS);

        return () => animation.cancel();
    }, [isAnimated]);

    const image = (
        <>
            <img src={plane.asset} alt="" className={imageStyle} />
            {plane.details.map((detail, index) => (
                <div key={index} className={layerStyle} style={getInsetStyle(detail.inset)}>
                    <img src={detail.asset} alt="" className={imageStyle} />
                </div>
            ))}
        </>
    );
    const insetStyle = getInsetStyle(plane.inset);

    if (!isAnimated) {
        return (
            <div className={layerStyle} style={insetStyle}>
                {image}
            </div>
        );
    }

    const [firstKeyframe] = PLANE_KEYFRAMES;
    const startPoseStyle = {
        ...insetStyle,
        "--layer-transform": firstKeyframe.transform,
        "--layer-opacity": firstKeyframe.opacity
    } as CSSProperties;

    return (
        <div ref={planeRef} className={cx(layerStyle, planeStyle)} style={startPoseStyle}>
            {image}
        </div>
    );
};

export const UnlockChoiceSentIllustration = () => {
    const shouldReduceMotion = useReducedMotion();
    const isAnimated = !shouldReduceMotion && supportsWebAnimations();

    return (
        <div aria-hidden="true" className={boxStyle}>
            <div className={sceneStyle}>
                {LAYERS.map((layer, index) => (
                    <UnlockChoiceSentIllustrationLayer key={index} layer={layer} isAnimated={isAnimated} />
                ))}
                <UnlockChoiceSentIllustrationPlane plane={UNLOCK_CHOICE_SENT_PLANE} isAnimated={isAnimated} />
            </div>
        </div>
    );
};

const boxStyle = css({
    position: "relative",
    width: "294.797px",
    height: "114px",
    marginLeft: "auto",
    marginRight: "auto",
    overflow: "visible",
    md: {
        width: "447px",
        height: "174px"
    }
});

const sceneStyle = css({
    position: "absolute",
    top: "0",
    left: "0",
    width: "294.797px",
    height: "114px",
    transformOrigin: "top left",
    md: {
        transform: "scale(1.5163)"
    }
});

const layerStyle = css({
    position: "absolute",
    top: "var(--layer-top)",
    right: "var(--layer-right)",
    bottom: "var(--layer-bottom)",
    left: "var(--layer-left)"
});

const animatedLayerStyle = css({
    willChange: "opacity"
});

const planeStyle = css({
    transform: "var(--layer-transform)",
    opacity: "var(--layer-opacity)"
});

const imageStyle = css({
    display: "block",
    width: "100%",
    height: "100%",
    maxWidth: "none"
});
import {type CSSProperties, useEffect, useRef} from "react";
import {css, cx} from "@panda/css";
import {motion, type TargetAndTransition, type Transition, useReducedMotion} from "framer-motion";

import {type AnimationEasing, type IKeyframeTrack, type IPoint, type IRect} from "@pg-mono/animation-utils";
import {noop} from "@pg-mono/nodash";

import {
    buildPlaneKeyframes,
    getPlaneAnimationDurationMs,
    getTrailRevealTimesMs,
    type IPlaneMotion,
    type IPlaneSilhouette
} from "./unlock_choice_sent_plane_motion";

type IllustrationTrackName = "opacity" | "rotate" | "scale" | "x" | "y";

type IllustrationTracks = Partial<Record<IllustrationTrackName, IKeyframeTrack>>;

export interface IUnlockChoiceSentLayer {
    asset: string;
    inset: [number, number, number, number];
    tracks?: IllustrationTracks;
}

export interface IUnlockChoiceSentPlane {
    asset: string;
    inset: [number, number, number, number];
    hull: IPoint[];
    details: IUnlockChoiceSentLayer[];
    motion: IPlaneMotion;
}

export const UNLOCK_CHOICE_SENT_DURATION_SECONDS = 2.5;

export const UNLOCK_CHOICE_SENT_SCENE = {width: 294.797, height: 114};

export const UNLOCK_CHOICE_SENT_TRAIL_FADE_MS = 60;

const TRAIL_CLEARANCE = 1;

const TRAIL_HIDDEN_PLANE_OPACITY = 0.05;

const TRACK_NAMES: IllustrationTrackName[] = ["opacity", "rotate", "scale", "x", "y"];

const CB: AnimationEasing = [0.5, 0, 0.5, 1];

export const UNLOCK_CHOICE_SENT_PLANE: IUnlockChoiceSentPlane = {
    asset: require("../../../images/unlock_choice_sent/plane.svg"),
    inset: [25.35, 0, 50.38, 86.3],
    hull: [
        {x: 0, y: 0.1909},
        {x: 0.0086, y: 0.1589},
        {x: 0.7737, y: 0.0122},
        {x: 1, y: 0.0002},
        {x: 1, y: 0.0346},
        {x: 0.7939, y: 0.6485},
        {x: 0.6545, y: 1},
        {x: 0.2703, y: 0.8087},
        {x: 0.2567, y: 0.7975}
    ],
    details: [{asset: require("../../../images/unlock_choice_sent/dot_right.svg"), inset: [22.539, 31.897, 71.899, 60.876]}],
    motion: {
        legs: [
            {
                start: 0.16,
                end: 0.6016,
                easeIn: 0.1,
                waypoints: [
                    {x: -270.319, y: 30.84, rotate: 87.376},
                    {x: -243.511, y: 52.093, rotate: 30.353},
                    {x: -186.519, y: 7.089, rotate: 4.171},
                    {x: -172.111, y: -2.428, rotate: -1.174}
                ]
            },
            {
                start: 0.6428,
                end: 0.975,
                approach: {start: 0.6304, rotate: 83.879},
                easeIn: 0.1,
                easeOut: 0.15,
                waypoints: [
                    {x: -101.34, y: 6.361, rotate: 86.91},
                    {x: -62.649, y: 27.629, rotate: 52.417},
                    {x: -42.186, y: 17.297, rotate: 33.709},
                    {x: -7.336, y: -0.298, rotate: -19.93}
                ]
            }
        ],
        scale: {
            values: [1, 0.7, 0.513, 0.4, 0.4, 1, 1],
            times: [0, 0.4, 0.56, 0.6304, 0.6428, 0.9084, 1],
            ease: [CB, CB, CB, "linear", CB, "linear"]
        },
        opacity: {
            values: [1, 1, 0, 0, 1, 1],
            times: [0, 0.56, 0.6016, 0.6304, 0.6428, 1],
            ease: ["linear", CB, "linear", CB, "linear"]
        },
        exit: {
            delayMs: 150,
            durationMs: 450,
            distance: 28,
            scale: 0.6,
            ease: [0.4, 0, 1, 1]
        }
    }
};

const FLIGHT_DURATION_MS = UNLOCK_CHOICE_SENT_DURATION_SECONDS * 1000;

export const UNLOCK_CHOICE_SENT_PLANE_DURATION_MS = getPlaneAnimationDurationMs(UNLOCK_CHOICE_SENT_PLANE.motion, FLIGHT_DURATION_MS);

const PLANE_KEYFRAMES = buildPlaneKeyframes(UNLOCK_CHOICE_SENT_PLANE.motion, FLIGHT_DURATION_MS);

const PLANE_ANIMATION_OPTIONS: KeyframeAnimationOptions = {
    duration: UNLOCK_CHOICE_SENT_PLANE_DURATION_MS,
    iterations: 1,
    fill: "forwards",
    easing: "linear"
};

const TIMELINE: Transition = {
    duration: UNLOCK_CHOICE_SENT_PLANE_DURATION_MS / 1000
};

const getInsetRect = (inset: [number, number, number, number]): IRect => {
    const [top, right, bottom, left] = inset;
    const {width, height} = UNLOCK_CHOICE_SENT_SCENE;

    return {
        left: (left / 100) * width,
        top: (top / 100) * height,
        width: ((100 - left - right) / 100) * width,
        height: ((100 - top - bottom) / 100) * height
    };
};

const getInsetCenter = (inset: [number, number, number, number]): IPoint => {
    const rect = getInsetRect(inset);

    return {x: rect.left + rect.width / 2, y: rect.top + rect.height / 2};
};

export const UNLOCK_CHOICE_SENT_PLANE_SILHOUETTE: IPlaneSilhouette = {
    box: getInsetRect(UNLOCK_CHOICE_SENT_PLANE.inset),
    hull: UNLOCK_CHOICE_SENT_PLANE.hull
};

const getTrailTrack = (revealMs: number): IKeyframeTrack => {
    const start = revealMs / UNLOCK_CHOICE_SENT_PLANE_DURATION_MS;
    const end = Math.min(1, (revealMs + UNLOCK_CHOICE_SENT_TRAIL_FADE_MS) / UNLOCK_CHOICE_SENT_PLANE_DURATION_MS);

    if (start <= 0) {
        return {values: [0, 1, 1], times: [0, end, 1], ease: [CB, "linear"]};
    }

    return {values: [0, 0, 1, 1], times: [0, start, end, 1], ease: ["linear", CB, "linear"]};
};

const withTrailTracks = (layers: IUnlockChoiceSentLayer[]): IUnlockChoiceSentLayer[] => {
    const revealTimesMs = getTrailRevealTimesMs(
        UNLOCK_CHOICE_SENT_PLANE.motion,
        FLIGHT_DURATION_MS,
        UNLOCK_CHOICE_SENT_PLANE_SILHOUETTE,
        layers.map((layer) => getInsetCenter(layer.inset)),
        {clearance: TRAIL_CLEARANCE, hiddenOpacity: TRAIL_HIDDEN_PLANE_OPACITY}
    );

    return layers.map((layer, index) => ({...layer, tracks: {opacity: getTrailTrack(revealTimesMs[index])}}));
};

export const UNLOCK_CHOICE_SENT_LAYERS = {
    still: [
        {asset: require("../../../images/unlock_choice_sent/circle.svg"), inset: [20.18, 41.65, 6.14, 29.85]},
        {asset: require("../../../images/unlock_choice_sent/spark_top_left.svg"), inset: [0.72, 53, 88.61, 44.93]},
        {asset: require("../../../images/unlock_choice_sent/spark_right.svg"), inset: [17.81, 40.9, 77.23, 55.34]},
        {asset: require("../../../images/unlock_choice_sent/envelopes.svg"), inset: [13.31, 41.75, 0, 28.39]},
        {asset: require("../../../images/unlock_choice_sent/dot_bottom_left.svg"), inset: [79.49, 67.99, 18.85, 31.28]},
        {asset: require("../../../images/unlock_choice_sent/spark_top_right.svg"), inset: [0, 45.39, 90.18, 51.92]}
    ] as IUnlockChoiceSentLayer[],
    leftDashes: withTrailTracks([
        {asset: require("../../../images/unlock_choice_sent/dash_left_01.svg"), inset: [51.39, 99.32, 43.46, 0]},
        {asset: require("../../../images/unlock_choice_sent/dash_left_02.svg"), inset: [59.62, 98.85, 35.31, 0.35]},
        {asset: require("../../../images/unlock_choice_sent/dash_left_03.svg"), inset: [67.21, 97.52, 28.3, 1.42]},
        {asset: require("../../../images/unlock_choice_sent/dash_left_04.svg"), inset: [73.71, 95.55, 22.66, 3.1]},
        {asset: require("../../../images/unlock_choice_sent/dash_left_05.svg"), inset: [78.46, 93.06, 18.75, 5.35]},
        {asset: require("../../../images/unlock_choice_sent/dash_left_06.svg"), inset: [81.31, 90.26, 16.6, 8]},
        {asset: require("../../../images/unlock_choice_sent/dash_left_07.svg"), inset: [82.05, 87.47, 16.11, 10.76]},
        {asset: require("../../../images/unlock_choice_sent/dash_left_08.svg"), inset: [79.96, 84.73, 17.37, 13.61]},
        {asset: require("../../../images/unlock_choice_sent/dash_left_09.svg"), inset: [76.11, 82.24, 20.47, 16.24]},
        {asset: require("../../../images/unlock_choice_sent/dash_left_10.svg"), inset: [70.92, 80.1, 25.31, 18.56]},
        {asset: require("../../../images/unlock_choice_sent/dash_left_11.svg"), inset: [65.1, 78.11, 30.88, 20.59]},
        {asset: require("../../../images/unlock_choice_sent/dash_left_12.svg"), inset: [58.91, 76.26, 36.83, 22.5]},
        {asset: require("../../../images/unlock_choice_sent/dash_left_13.svg"), inset: [53.14, 74.32, 42.99, 24.37]},
        {asset: require("../../../images/unlock_choice_sent/dash_left_14.svg"), inset: [47.81, 72.2, 48.51, 26.39]},
        {asset: require("../../../images/unlock_choice_sent/dash_left_15.svg"), inset: [43.16, 69.88, 53.48, 28.61]}
    ]),
    rightDashes: withTrailTracks([
        {asset: require("../../../images/unlock_choice_sent/dash_right_01.svg"), inset: [41.27, 40.13, 55.54, 58.36]},
        {asset: require("../../../images/unlock_choice_sent/dash_right_02.svg"), inset: [45.58, 37.67, 51.27, 60.75]},
        {asset: require("../../../images/unlock_choice_sent/dash_right_03.svg"), inset: [49.95, 35.35, 46.79, 63.17]},
        {asset: require("../../../images/unlock_choice_sent/dash_right_04.svg"), inset: [54.26, 32.9, 42.56, 65.52]},
        {asset: require("../../../images/unlock_choice_sent/dash_right_05.svg"), inset: [58.01, 30.34, 39.14, 68.06]},
        {asset: require("../../../images/unlock_choice_sent/dash_right_06.svg"), inset: [60.77, 27.55, 37, 70.72]},
        {asset: require("../../../images/unlock_choice_sent/dash_right_07.svg"), inset: [62.21, 24.7, 36.11, 73.54]},
        {asset: require("../../../images/unlock_choice_sent/dash_right_08.svg"), inset: [61.93, 21.83, 36.27, 76.34]},
        {asset: require("../../../images/unlock_choice_sent/dash_right_09.svg"), inset: [60.18, 19.09, 37.61, 79.18]},
        {asset: require("../../../images/unlock_choice_sent/dash_right_10.svg"), inset: [57.08, 16.48, 39.94, 81.91]},
        {asset: require("../../../images/unlock_choice_sent/dash_right_11.svg"), inset: [52.86, 14.05, 43.68, 84.5]},
        {asset: require("../../../images/unlock_choice_sent/dash_right_12.svg"), inset: [47.45, 11.95, 48.42, 86.76]}
    ])
};

const LAYERS: IUnlockChoiceSentLayer[] = [
    ...UNLOCK_CHOICE_SENT_LAYERS.still,
    ...UNLOCK_CHOICE_SENT_LAYERS.leftDashes,
    ...UNLOCK_CHOICE_SENT_LAYERS.rightDashes
];

const supportsWebAnimations = () => typeof HTMLElement !== "undefined" && typeof HTMLElement.prototype.animate === "function";

const getInsetStyle = (inset: [number, number, number, number]): CSSProperties => {
    const [top, right, bottom, left] = inset;

    return {
        "--layer-top": `${top}%`,
        "--layer-right": `${right}%`,
        "--layer-bottom": `${bottom}%`,
        "--layer-left": `${left}%`
    } as CSSProperties;
};

const getInitial = (tracks: IllustrationTracks): TargetAndTransition => {
    const initial: TargetAndTransition = {};

    for (const name of TRACK_NAMES) {
        const track = tracks[name];

        if (track) {
            initial[name] = track.values[0];
        }
    }

    return initial;
};

const getAnimate = (tracks: IllustrationTracks): TargetAndTransition => {
    const animate: TargetAndTransition = {};

    for (const name of TRACK_NAMES) {
        const track = tracks[name];

        if (track) {
            animate[name] = track.values;
        }
    }

    return animate;
};

const getTransition = (tracks: IllustrationTracks): Transition => {
    const transition: Partial<Record<IllustrationTrackName, Transition>> = {};

    for (const name of TRACK_NAMES) {
        const track = tracks[name];

        if (track) {
            transition[name] = {...TIMELINE, times: track.times, ease: track.ease};
        }
    }

    return transition;
};

interface ILayerProps {
    layer: IUnlockChoiceSentLayer;
    isAnimated: boolean;
}

const UnlockChoiceSentIllustrationLayer = (props: ILayerProps) => {
    const {layer, isAnimated} = props;
    const insetStyle = getInsetStyle(layer.inset);
    const image = <img src={layer.asset} alt="" className={imageStyle} />;

    if (!isAnimated || !layer.tracks) {
        return (
            <div className={layerStyle} style={insetStyle}>
                {image}
            </div>
        );
    }

    return (
        <motion.div
            className={cx(layerStyle, animatedLayerStyle)}
            style={insetStyle}
            initial={getInitial(layer.tracks)}
            animate={getAnimate(layer.tracks)}
            transition={getTransition(layer.tracks)}
            /**
                Any onUpdate makes framer-motion animate in JS instead of WAAPI. On WAAPI finish it cancels the animation at once but writes the final
                opacity a frame later, so for that frame the dash falls back to its `initial` opacity of 0 and the trail blinks
            **/
            onUpdate={noop}
        >
            {image}
        </motion.div>
    );
};

interface IPlaneProps {
    plane: IUnlockChoiceSentPlane;
    isAnimated: boolean;
}

const UnlockChoiceSentIllustrationPlane = (props: IPlaneProps) => {
    const {plane, isAnimated} = props;
    const planeRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const element = planeRef.current;

        if (!isAnimated || !element) {
            return;
        }

        const animation = element.animate(PLANE_KEYFRAMES, PLANE_ANIMATION_OPTIONS);

        return () => animation.cancel();
    }, [isAnimated]);

    const image = (
        <>
            <img src={plane.asset} alt="" className={imageStyle} />
            {plane.details.map((detail, index) => (
                <div key={index} className={layerStyle} style={getInsetStyle(detail.inset)}>
                    <img src={detail.asset} alt="" className={imageStyle} />
                </div>
            ))}
        </>
    );
    const insetStyle = getInsetStyle(plane.inset);

    if (!isAnimated) {
        return (
            <div className={layerStyle} style={insetStyle}>
                {image}
            </div>
        );
    }

    const [firstKeyframe] = PLANE_KEYFRAMES;
    const startPoseStyle = {
        ...insetStyle,
        "--layer-transform": firstKeyframe.transform,
        "--layer-opacity": firstKeyframe.opacity
    } as CSSProperties;

    return (
        <div ref={planeRef} className={cx(layerStyle, planeStyle)} style={startPoseStyle}>
            {image}
        </div>
    );
};

export const UnlockChoiceSentIllustration = () => {
    const shouldReduceMotion = useReducedMotion();
    const isAnimated = !shouldReduceMotion && supportsWebAnimations();

    return (
        <div aria-hidden="true" className={boxStyle}>
            <div className={sceneStyle}>
                {LAYERS.map((layer, index) => (
                    <UnlockChoiceSentIllustrationLayer key={index} layer={layer} isAnimated={isAnimated} />
                ))}
                <UnlockChoiceSentIllustrationPlane plane={UNLOCK_CHOICE_SENT_PLANE} isAnimated={isAnimated} />
            </div>
        </div>
    );
};

const boxStyle = css({
    position: "relative",
    width: "294.797px",
    height: "114px",
    marginLeft: "auto",
    marginRight: "auto",
    overflow: "visible",
    md: {
        width: "447px",
        height: "174px"
    }
});

const sceneStyle = css({
    position: "absolute",
    top: "0",
    left: "0",
    width: "294.797px",
    height: "114px",
    transformOrigin: "top left",
    md: {
        transform: "scale(1.5163)"
    }
});

const layerStyle = css({
    position: "absolute",
    top: "var(--layer-top)",
    right: "var(--layer-right)",
    bottom: "var(--layer-bottom)",
    left: "var(--layer-left)"
});

const animatedLayerStyle = css({
    willChange: "opacity"
});

const planeStyle = css({
    transform: "var(--layer-transform)",
    opacity: "var(--layer-opacity)"
});

const imageStyle = css({
    display: "block",
    width: "100%",
    height: "100%",
    maxWidth: "none"
});
